import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUnattemptedQuizzes } from '../apiClient';

interface UnattemptedQuiz {
  id: number;
  name: string;
  description: string;
  subject_id: number;
  start_date: string | null;
  quiz_detail_image: string;
  is_live: number;
  marking: string;
  quiz_type: string;
  user_id: number;
  quiz_reference: string;
  exam_id: number;
  level: string;
  total_questions: number;
  subject_names: string;
}

interface UnattemptedQuizSectionProps {
  // No props needed now - component manages its own data
}

const UnattemptedQuizSection: React.FC<UnattemptedQuizSectionProps> = () => {
  const navigate = useNavigate();
  const [unattemptedQuizzes, setUnattemptedQuizzes] = useState<UnattemptedQuiz[]>([]);
  const [isLoadingUnattempted, setIsLoadingUnattempted] = useState(false);
  const [confirmingQuiz, setConfirmingQuiz] = useState<UnattemptedQuiz | null>(null);

  useEffect(() => {
    fetchUnattemptedQuizzes();
  }, []);

  const fetchUnattemptedQuizzes = async () => {
    setIsLoadingUnattempted(true);
    try {
      const response = await getUnattemptedQuizzes();
      if (response && response.result) {
        setUnattemptedQuizzes(response.result);
      }
    } catch (error) {
      console.error('Failed to fetch unattempted quizzes:', error);
    } finally {
      setIsLoadingUnattempted(false);
    }
  };

  const handleStartQuiz = (quiz: UnattemptedQuiz) => {
    setConfirmingQuiz(quiz);
  };

  const handleConfirmStart = () => {
    if (confirmingQuiz?.quiz_reference) {
      navigate(`/quiz/${confirmingQuiz.quiz_reference}`);
      setConfirmingQuiz(null);
    }
  };

  const handleCancelStart = () => {
    setConfirmingQuiz(null);
  };

  const formatQuizDescription = (description: string) => {
    if (!description) return 'No description available';
    return description.length > 100 ? `${description.substring(0, 100)}...` : description;
  };

  const getLevelBadgeColor = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'elementary':
      case 'elementry': // Handle typo in database
        return 'bg-success';
      case 'intermediate':
        return 'bg-warning';
      case 'advanced':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  const getMarkingBadgeColor = (marking: string) => {
    switch (marking?.toLowerCase()) {
      case 'regular':
        return 'bg-primary';
      case 'negative':
        return 'bg-warning';
      default:
        return 'bg-info';
    }
  };

  return (
    <>
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
            <div className="card-header bg-transparent border-0 p-4">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-0">
                  <i className="fas fa-play-circle me-2 text-success"></i>
                  Unattempted Quizzes
                </h5>
                <div className="d-flex gap-2">
                  <button 
                    onClick={fetchUnattemptedQuizzes}
                    className="btn btn-outline-secondary btn-sm"
                    style={{ borderRadius: '8px' }}
                    disabled={isLoadingUnattempted}>
                    <i className="fas fa-refresh me-1"></i>
                    Refresh
                  </button>
                  <Link 
                    to="/custom-quiz-generator" 
                    className="btn btn-outline-primary btn-sm"
                    style={{ borderRadius: '8px' }}>
                    <i className="fas fa-plus me-1"></i>
                    Create New
                  </Link>
                </div>
              </div>
            </div>
            <div className="card-body p-4">
              {isLoadingUnattempted ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2 mb-0">Loading unattempted quizzes...</p>
                </div>
              ) : unattemptedQuizzes.length > 0 ? (
                <div className="row g-3">
                  {unattemptedQuizzes.map((quiz) => (
                    <div key={quiz.id} className="col-12 col-sm-6 col-lg-4">
                      <div className="card border h-100" style={{ 
                        borderRadius: '12px', 
                        borderColor: '#e9ecef',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '';
                      }}>
                        <div className="card-body p-3" style={{ minHeight: '150px' }}>
                          <div className="d-flex align-items-start justify-content-between mb-2">
                            <div className="flex-grow-1 me-2">
                              <h6 className="fw-bold mb-1" style={{ 
                                fontSize: '0.9rem',
                                lineHeight: '1.3',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}>
                                {quiz.name}
                              </h6>
                              <p className="text-muted mb-2" style={{ 
                                fontSize: '0.8rem',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical'
                              }}>
                                {formatQuizDescription(quiz.description)}
                              </p>
                              {quiz.subject_names && (
                                <p className="text-primary mb-2" style={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}>
                                  <i className="fas fa-book me-1"></i>
                                  {quiz.subject_names}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Quiz badges */}
                          <div className="d-flex flex-wrap gap-1 mb-2">
                            <span className={`badge ${getLevelBadgeColor(quiz.level)} text-white`} 
                              style={{ fontSize: '0.7rem' }}>
                              {quiz.level || 'Elementary'}
                            </span>
                            {quiz.quiz_type && (
                              <span className="badge bg-light text-dark" 
                                style={{ fontSize: '0.7rem' }}>
                                {quiz.quiz_type}
                              </span>
                            )}
                            {quiz.total_questions && (
                              <span className="badge bg-info text-white" 
                                style={{ fontSize: '0.7rem' }}>
                                <i className="fas fa-question-circle me-1"></i>
                                {quiz.total_questions} Questions
                              </span>
                            )}
                          </div>

                          {/* Action button */}
                          <div className="d-flex justify-content-between align-items-center mt-auto">
                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                              <i className="fas fa-calendar-plus me-1"></i>
                              Ready to start
                            </small>
                            <button 
                              onClick={() => handleStartQuiz(quiz)}
                              className="btn btn-success btn-sm"
                              style={{ 
                                borderRadius: '6px', 
                                fontSize: '0.7rem', 
                                padding: '0.35rem 0.7rem',
                                fontWeight: '600'
                              }}>
                              <i className="fas fa-play me-1"></i>
                              Start Quiz
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                    style={{ 
                      width: '80px', 
                      height: '80px',
                      backgroundColor: '#f8f9fa',
                      border: '2px dashed #dee2e6'
                    }}>
                    <i className="fas fa-clipboard-check text-muted fa-2x"></i>
                  </div>
                  <h6 className="text-muted mb-2">All Quizzes Attempted!</h6>
                  <p className="text-muted mb-3">Great job! You've attempted all your created quizzes.</p>
                  <Link 
                    to="/custom-quiz-generator" 
                    className="btn btn-success"
                    style={{ borderRadius: '8px' }}>
                    <i className="fas fa-plus me-2"></i>
                    Create New Quiz
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmingQuiz && (
        <div 
          className="modal show d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={handleCancelStart}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ borderRadius: '15px' }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">
                  <i className="fas fa-play-circle me-2 text-success"></i>
                  Start Quiz
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCancelStart}
                  aria-label="Close">
                </button>
              </div>
              <div className="modal-body pt-2">
                <div className="alert alert-light border-0" style={{ backgroundColor: '#f8f9fa' }}>
                  <h6 className="fw-bold mb-2">{confirmingQuiz.name}</h6>
                  <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                    {formatQuizDescription(confirmingQuiz.description)}
                  </p>
                  {confirmingQuiz.subject_names && (
                    <p className="text-primary mb-3" style={{ 
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}>
                      <i className="fas fa-book me-1"></i>
                      <strong>Subjects:</strong> {confirmingQuiz.subject_names}
                    </p>
                  )}
                  <div className="d-flex gap-2 mb-0">
                    <span className={`badge ${getLevelBadgeColor(confirmingQuiz.level)} text-white`}>
                      {confirmingQuiz.level || 'Elementary'}
                    </span>
                    <span className={`badge ${getMarkingBadgeColor(confirmingQuiz.marking)} text-white`}>
                      {confirmingQuiz.marking}
                    </span>
                    {confirmingQuiz.total_questions && (
                      <span className="badge bg-info text-white">
                        <i className="fas fa-question-circle me-1"></i>
                        {confirmingQuiz.total_questions} Questions
                      </span>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-center mb-3">
                  <div className="text-center">
                    <i className="fas fa-info-circle text-primary fa-2x mb-2"></i>
                    <p className="mb-0" style={{ fontSize: '0.95rem' }}>
                      Are you ready to start this quiz? Once started, your progress will be tracked.
                    </p>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={handleCancelStart}
                  style={{ borderRadius: '8px' }}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-success"
                  onClick={handleConfirmStart}
                  style={{ borderRadius: '8px' }}>
                  <i className="fas fa-play me-2"></i>
                  Yes, Start Quiz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UnattemptedQuizSection;